import { ArrowLeft, LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './Button';
import type { ReactNode } from 'react';
import type { NavigationContext } from '@/types/navigation';

interface DetailHeaderProps {
  // Back button
  onBack: () => void;
  backLabel?: string;

  // Dynamic back button (from navigation context)
  previousContext?: NavigationContext;

  // Header content
  icon?: LucideIcon;
  iconSize?: number;
  thumbnailUrl?: string;
  title: string;
  description?: string | null;
  metadata?: ReactNode;

  // Actions
  actions?: ReactNode;
}

export function DetailHeader({
  onBack,
  backLabel = 'Back',
  previousContext,
  icon: Icon,
  iconSize = 12,
  thumbnailUrl,
  title,
  description,
  metadata,
  actions,
}: DetailHeaderProps) {
  const navigate = useNavigate();

  const handlePreviousContextClick = () => {
    if (previousContext) {
      navigate(previousContext.path);
    }
  };

  return (
    <>
      {/* Back button bar */}
      <div className="border-b border-border bg-background-subtle px-4 py-3">
        <div className="flex items-center gap-2">
          {/* Dynamic back button (from navigation context) */}
          {previousContext && (
            <>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
                onClick={handlePreviousContextClick}
              >
                {previousContext.label}
              </Button>
              {/* Separator */}
              <div className="h-6 w-px bg-border" />
            </>
          )}

          {/* Static back button (fallback) */}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={onBack}
          >
            {backLabel}
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-border bg-background-subtle px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {/* Icon/Thumbnail */}
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-lg bg-background overflow-hidden">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className="h-full w-full object-cover"
                />
              ) : Icon ? (
                <Icon className={`h-${iconSize} w-${iconSize} text-foreground-muted`} />
              ) : null}
            </div>

            {/* Title and description */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-foreground truncate">
                {title}
              </h2>
              {description && (
                <p className="mt-1 text-sm text-foreground-muted line-clamp-2">
                  {description}
                </p>
              )}
              {metadata && (
                <div className="mt-2">
                  {metadata}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
